import { lowerFirst } from 'lodash';
import { Dispatch, SetStateAction } from 'react';
import { UseFormReset, UseFormSetValue } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { ICliJob } from 'cli/types';
import { NotificationPosition } from 'core/enums';
import { useCliJobHandler } from 'core/hooks';
import { FormProvider, useAgGridTableContext, useNotificationContext } from 'core/states';
import { IConfirmation, IConfirmationAction } from 'core/types';
import { getFullUrl, goTo, isEmptyArray } from 'core/utils';
import { PricingProfileDetailsTab } from 'pricing/enums';
import { pricingProfileDetailsPath } from 'pricing/routes/routePaths';
import { IPricingProfile } from 'pricing/types';
import {
  QuoteEntityType,
  QuotePartAddToPricingProfileSubmitType,
  QuotePartListSelectionAction,
} from 'quotes/enums';
import { useQuotePartActions } from 'quotes/hooks';
import { mapQuotePartAddToPricingProfile, mapQuotePartGetDiscountedParts } from 'quotes/mappers';
import { IAddToPricingProfileForm, IDiscountedQuotePart } from 'quotes/types';
import { Color } from 'theme/enums';

import QuotePartAddToPricingProfileFormView from './QuotePartAddToPricingProfileFormView';
import { quotePartAddToPricingProfileSchema } from './schema';

interface IProps {
  isOpen?: boolean;
  onClose?: (closeSelection?: boolean) => void;
  setConfirmationOption?: Dispatch<SetStateAction<IConfirmation>>;
  setActiveSelectionActionDrawer?: Dispatch<SetStateAction<QuotePartListSelectionAction>>;
  defaultPricingProfile?: IPricingProfile;
  setDefaultPricingProfile?: Dispatch<SetStateAction<IPricingProfile>>;
  onSubmitSuccess: () => void;
  quoteEntityType?: QuoteEntityType;
}

const QuotePartAddToPricingProfileForm = ({
  isOpen,
  onClose,
  setConfirmationOption,
  setActiveSelectionActionDrawer,
  defaultPricingProfile,
  setDefaultPricingProfile,
  onSubmitSuccess,
  quoteEntityType = QuoteEntityType.QUOTE,
}: IProps) => {
  const { t } = useTranslation();

  const { clearNotificationTimeout, showSuccessNotification } = useNotificationContext();
  const { selectedRows } = useAgGridTableContext();

  const { handleCliJob } = useCliJobHandler();
  const { addToPricingProfile, fetchDiscountedParts } = useQuotePartActions();

  const onPricingOptionsChange = (
    newValue: string,
    oldValue: string,
    setValue: UseFormSetValue<IAddToPricingProfileForm>
  ) => {
    if (newValue !== oldValue) {
      setValue('pricingOptions', newValue);
    }

    if (newValue === oldValue) {
      setValue('pricingOptions', '');
    }
  };

  const onSubmit =
    (reset: UseFormReset<IAddToPricingProfileForm>) => async (data: IAddToPricingProfileForm) => {
      const existingDiscountedParts = await fetchExistingDiscountedParts(data);

      if (isEmptyArray(existingDiscountedParts)) {
        saveToPricingProfile(reset, data);

        return;
      }

      const allSelectedPartsDiscounted = selectedRows.length === existingDiscountedParts.length;

      setConfirmationOption({
        title: t('titleInfo'),
        message: t('messagePartAlreadyAddedToPricingProfile', {
          partNumbers: existingDiscountedParts.map((part) => part.number).join(', '),
        }),
        confirmBtnColor: Color.SUCCESS,
        cancelBtnLabel: t('buttonLabelCancel'),
        onCancel: onConfirmationCancel,
        confirmBtnLabel: allSelectedPartsDiscounted ? t('buttonLabelUpdate') : null,
        onConfirm: allSelectedPartsDiscounted
          ? onUpdateExistingDiscountedPartsConfirm(reset, data)
          : null,
        confirmationActions: allSelectedPartsDiscounted
          ? null
          : getConfirmationActions(reset, data, existingDiscountedParts),
      });
    };

  const fetchExistingDiscountedParts = async (data: IAddToPricingProfileForm) => {
    const { success, resultRecords } = await fetchDiscountedParts(
      mapQuotePartGetDiscountedParts(data, selectedRows)
    );

    if (!success) {
      return null;
    }

    return resultRecords.map((record) => record.quotePart);
  };

  const onConfirmationCancel = () => {
    clearNotificationTimeout();

    setConfirmationOption(null);
  };

  const onUpdateExistingDiscountedPartsConfirm =
    (reset: UseFormReset<IAddToPricingProfileForm>, data: IAddToPricingProfileForm) => () => {
      setConfirmationOption(null);

      saveToPricingProfile(reset, data);
    };

  const getConfirmationActions = (
    reset: UseFormReset<IAddToPricingProfileForm>,
    formData: IAddToPricingProfileForm,
    existingDiscountedParts: IDiscountedQuotePart[]
  ): IConfirmationAction[] => {
    return [
      {
        type: QuotePartAddToPricingProfileSubmitType.ADD_ALL,
        label: t('buttonLabelAddToPricingProfileAll'),
        onClick: () => saveToPricingProfile(reset, formData),
      },
      {
        type: QuotePartAddToPricingProfileSubmitType.ADD_NEW,
        label: t('buttonLabelAddToPricingProfileNew'),
        onClick: () => {
          const discountedPartIds = existingDiscountedParts.map((part) => part.id);

          saveToPricingProfile(
            reset,
            formData,
            selectedRows.filter((part) => !discountedPartIds.includes(part.id))
          );
        },
      },
    ];
  };

  const saveToPricingProfile = async (
    reset: UseFormReset<IAddToPricingProfileForm>,
    data: IAddToPricingProfileForm,
    quoteParts = selectedRows
  ) => {
    handleCliJob(addToPricingProfile, mapQuotePartAddToPricingProfile(quoteParts, data), {
      cliJobInProgressMessage: t('messageQuotePartAddToPricingProfileInProgress', {
        count: quoteParts.length,
        entityType: lowerFirst(quoteEntityType),
      }),
      onCliJobFinished: (cliJob: ICliJob) => {
        const pricingProfile = (cliJob.result as IPricingProfile[])[0];

        showSuccessNotification(
          {
            message: t('messageQuotePartAddToPricingProfileSuccess', {
              count: quoteParts.length,
              entityType: quoteEntityType,
            }),
          },
          NotificationPosition.TOP
        );

        onSubmitSuccess();

        setConfirmationOption({
          title: t('titleInfo'),
          message: t('messageReviewPricingProfileAccess'),
          confirmBtnColor: Color.SUCCESS,
          confirmBtnLabel: t('buttonLabelReviewAccess'),
          cancelBtnLabel: t('buttonLabelReturnToQuote', { entityType: quoteEntityType }),
          onConfirm: () => {
            setConfirmationOption(null);

            goTo(
              getFullUrl(
                pricingProfileDetailsPath(pricingProfile.id, PricingProfileDetailsTab.ACCESS)
              ),
              true
            );
          },
          onCancel: () => setConfirmationOption(null),
        });
      },
    });

    handleOnClose(reset, true);
  };

  const onAddNewPricingProfile = (option) => {
    setActiveSelectionActionDrawer(QuotePartListSelectionAction.CREATE_PRICING_PROFILE);

    setDefaultPricingProfile(option);
  };

  const handleOnClose = (
    reset?: UseFormReset<IAddToPricingProfileForm>,
    closeSelection = false
  ) => {
    setDefaultPricingProfile(null);

    reset();

    onClose(closeSelection);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <FormProvider schema={quotePartAddToPricingProfileSchema()}>
      <QuotePartAddToPricingProfileFormView
        isOpen={isOpen}
        onClose={handleOnClose}
        onSubmit={onSubmit}
        onPricingOptionsChange={onPricingOptionsChange}
        onAddNewPricingProfile={onAddNewPricingProfile}
        defaultPricingProfile={defaultPricingProfile}
      />
    </FormProvider>
  );
};

export default QuotePartAddToPricingProfileForm;
